import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { catchError, map, mergeMap, withLatestFrom } from 'rxjs/operators';
import { DiagramTreeService } from '../../utils/services/diagram-tree.service';
import { DiagramEditorWorkflowMenuService } from '../../utils/services/diagram-editor-workflow-menu.service';

import * as DiagramActions from './diagram.actions';
import * as UIDataStoreActions from './../../../../../project/src/lib/states/ui-data-store/ui-data-store.actions';
import * as UIStepFunctionActions from './../../../../../project/src/lib/states/ui-step-functions/ui-step-function.actions';
import * as UIDataEventActions from './../../../../../project/src/lib/states/ui-data-event-state/ui-data-event.actions';
import * as AuthenticationActions from 'libs/authentication/src/lib/state/authentication.actions';
import { WorkflowService } from 'libs/workflow-diagram/src/lib/services/workflow.service'
import {
  UiDataEventControllerService,
  WorkflowControllerService,
  CopyServerSideWorkflowRequestDto
} from '@rappider/rappider-sdk';
import { NotificationService } from '@rappider/services';

@Injectable()
export class DiagramEffects {
  constructor(
    private actions$: Actions,
    private store: Store<any>,
    private diagramTreeService: DiagramTreeService,
    private diagramEditorWorkflowMenuService: DiagramEditorWorkflowMenuService,
    private uiDataEventControllerService: UiDataEventControllerService,
    private workflowControllerService: WorkflowControllerService,
    private notificationService: NotificationService,
    private workflowService: WorkflowService
  ) { }

  buildDiagramTree$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        DiagramActions.BuildDiagramTree,
        UIDataEventActions.ActionTypes.SetUIDataEvents,
        UIDataEventActions.ActionTypes.CreateUIDataEventSuccessful,
        UIDataEventActions.ActionTypes.BulkCreateUIDataEventSuccessful,
        UIDataEventActions.ActionTypes.UpdateUIDataEventSuccessful,
        UIDataEventActions.ActionTypes.DeleteUIDataEventSuccessful,
        UIStepFunctionActions.ActionTypes.GetUIWorkflowStepFunctionsSuccessful,
        UIStepFunctionActions.ActionTypes.GetUpdatedUIWorkflowStepFunctionWithDetailsSuccessful,
        UIStepFunctionActions.ActionTypes.CreateUIWorkflowStepFunctionSuccessful,
        UIStepFunctionActions.ActionTypes.BulkCreateUIWorkflowStepFunctionSuccessful,
        UIStepFunctionActions.ActionTypes.UpdateUIWorkflowStepFunctionSuccessful,
        UIStepFunctionActions.ActionTypes.DeleteUIWorkflowStepFunctionSuccessful,
        UIDataStoreActions.ActionTypes.CreateUIDataStoresAccordingToIdSuccessful
      ),
      withLatestFrom(
        this.store.select(state => state.uiDataEvent?.isLoaded),
        this.store.select(state => state.uiDataEvent?.data),
        this.store.select(state => state.uiWorkflowStepFunction?.isLoaded),
        this.store.select(state => state.uiWorkflowStepFunction?.data)
      ),
      mergeMap(([
        action,
        isUiDataEventsLoaded,
        uiDataEvents,
        isUiStepFunctionsLoaded,
        uiStepFunctions
      ]) => {
        if (isUiDataEventsLoaded && isUiStepFunctionsLoaded) {
          return this.diagramTreeService.buildDiagramTree(uiDataEvents, uiStepFunctions).pipe(
            map(diagramTreeData => DiagramActions.BuildDiagramTreeSuccessful({
              payload: {
                diagramTree: diagramTreeData.diagramTree,
                connectors: diagramTreeData.connectedNodes
              }
            })),
            catchError(error => [
              DiagramActions.ErrorAction({ payload: { error, key: 'BuildDiagramTreeFailure', timeStamp: Date.now() } })
            ])
          );
        } else {
          return [DiagramActions.BuildDiagramTreeFailure({ payload: { error: 'isUiDataEventsLoaded or isUiStepFunctionsLoaded could not found', key: 'BuildDiagramTreeFailure', timeStamp: Date.now() } })];
        }
      })
    )
  );

  populateWorkflowMenu$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        DiagramActions.PopulateWorkflowMenu,
        UIDataEventActions.ActionTypes.SetUIDataEvents,
        UIDataEventActions.ActionTypes.CreateUIDataEventSuccessful,
        UIDataEventActions.ActionTypes.BulkCreateUIDataEventSuccessful,
        UIDataEventActions.ActionTypes.UpdateUIDataEventSuccessful,
        UIDataEventActions.ActionTypes.DeleteUIDataEventSuccessful,
        UIStepFunctionActions.ActionTypes.GetUIWorkflowStepFunctionsSuccessful,
        UIStepFunctionActions.ActionTypes.GetUpdatedUIWorkflowStepFunctionWithDetailsSuccessful,
        UIStepFunctionActions.ActionTypes.CreateUIWorkflowStepFunctionSuccessful,
        UIStepFunctionActions.ActionTypes.BulkCreateUIWorkflowStepFunctionSuccessful,
        UIStepFunctionActions.ActionTypes.UpdateUIWorkflowStepFunctionSuccessful,
        UIStepFunctionActions.ActionTypes.DeleteUIWorkflowStepFunctionSuccessful,
        UIDataStoreActions.ActionTypes.CreateUIDataStoresAccordingToIdSuccessful
      ),
      withLatestFrom(
        this.store.select(state => state.uiDataStore?.data),
        this.store.select(state => state.uiDataEvent?.isLoaded),
        this.store.select(state => state.uiDataEvent?.data),
        this.store.select(state => state.uiWorkflowStepFunction?.isLoaded),
        this.store.select(state => state.uiWorkflowStepFunction?.data)
      ),
      mergeMap(([
        action,
        uiDataStores,
        isUiDataEventsLoaded,
        uiDataEvents,
        isUiStepFunctionsLoaded,
        uiStepFunctions
      ]) => {
        if (isUiDataEventsLoaded && isUiStepFunctionsLoaded && uiDataStores.length) {
          return this.diagramEditorWorkflowMenuService
            .populateDiagramWorkflowMenu(uiDataStores, uiStepFunctions, uiDataEvents).pipe(
              map(workflowMenu => DiagramActions.PopulateWorkflowMenuSuccessful({
                payload: { workflowMenu: workflowMenu }
              })),
              catchError(error => [
                DiagramActions.ErrorAction({ payload: { error: error, key: 'PopulateWorkflowMenuFailure', timeStamp: Date.now() } })
              ])
            );
        } else {
          return [DiagramActions.PopulateWorkflowMenuFailure({ payload: { error: 'There is no isUiDataEventsLoaded, isUiStepFunctionsLoaded or uiDataStores', key: 'PopulateWorkflowMenuFailure', timestamp: Date.now() } })];
        }

      })
    )
  );

  duplicateWorkflow$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        DiagramActions.DuplicateWorkflow
      ),
      mergeMap((action) => {
        const params: CopyServerSideWorkflowRequestDto = {
          eventId: action.payload.eventId
        };
        return this.workflowControllerService.copyServerSideWorkflow({ body: params }).pipe(
          mergeMap(() => {
            this.notificationService.createNotification(
              'success',
              'Success',
              'Workflow Created Successfully'
            );
            const publishedActions: Action[] = [
              new UIDataEventActions.GetUIDataEvents(),
              new UIStepFunctionActions.GetUIWorkflowStepFunctions(),
              DiagramActions.DuplicateWorkflowSuccessful()
            ];
            return publishedActions;
          }), catchError(error => {
            this.notificationService.createNotification(
              'error',
              'Failure',
              'Failed to create a Workflow'
            );
            return [
              DiagramActions.DuplicateWorkflowFailure({ payload: { error, key: 'DuplicateWorkflow', timestamp: Date.now() } })
            ];
          }
          ));
      }
      )
    )
  );

  generateWorkflowWithAI$ = createEffect(() => this.actions$.pipe(
    ofType(DiagramActions.GenerateWorkflowWithAI),
    mergeMap((action: any) => {
      const params = {
        body: {
          message: action.payload.aiPrompt,
          programmingLanguage: action.payload.programmingLanguage || 'nodejs:20',
          force: action.payload.force || false,
          uiDataEventId: action.payload.uiDataEventId || undefined
        }
      };

      return this.workflowControllerService.generateByAI(params).pipe(
        mergeMap((response) => {
          this.notificationService.createNotification(
            'success',
            'AI Workflow Generation',
            'Workflow has been generated successfully with Assistant'
          );

          return [
            DiagramActions.GenerateWorkflowWithAISuccessful({
              payload: {
                generatedWorkflow: response,
                projectId: action.payload.projectId
              }
            }),
            new UIDataEventActions.GetUIDataEvents(),
            new UIStepFunctionActions.GetUIWorkflowStepFunctions()
          ];
        }),
        catchError(error => {
          this.notificationService.createNotification(
            'error',
            'AI Workflow Generation',
            error?.error?.message || 'Failed to generate workflow with Assistant'
          );

          return [
            DiagramActions.GenerateWorkflowWithAIFailure({
              payload: {
                error: error,
                key: 'GenerateWorkflowWithAI',
                timestamp: Date.now(),
                projectId: action.payload.projectId
              }
            })
          ];
        })
      );
    })
  ));

  deleteWorkflowByEventId$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        DiagramActions.DeleteWorkflowByEventId
      ),
      withLatestFrom(
        this.store.select(state => state.activeProject?.data?.id)
      ),
      mergeMap(([action, activeProjectId]) => {
        return this.workflowControllerService.deleteWorkflowByWorkflowEventId({ body: { workflowEventId: action.payload.id } }).pipe(
          map(() => {
            this.notificationService.createNotification(
              'success',
              'Success',
              'Successful to delete a Workflow'
            );
            this.workflowService.refreshSyncedRemoteData(activeProjectId);
            return DiagramActions.DeleteWorkflowByEventIdSuccessful;
          }), catchError(error => {
            this.notificationService.createNotification(
              'error',
              'Failure',
              'Failed to delete a Workflow'
            );
            return [
              DiagramActions.DeleteWorkflowByEventIdFailure({ payload: { error, key: 'DeleteWorkflowByEventIdFailure', timestamp: Date.now() } })
            ];
          }
          ));
      }
      )
    )
  );

}
